import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";

const CHUNK_RELOAD_KEY = "app-chunk-reload";

// When the dev server restarts (or a new build replaces hashed asset URLs),
// already-loaded tabs fail to lazy-load route chunks with "Failed to fetch
// dynamically imported module". Auto-reload once to pick up fresh URLs
// instead of leaving the user on a blank screen.
function isChunkLoadError(message: string | undefined) {
  if (!message) return false;
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Importing a module script failed")
  );
}

function handleChunkLoadFailure() {
  if (typeof window === "undefined") return;
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || "0");
  // Only reload once per minute to avoid loops if the chunk is genuinely broken.
  if (Date.now() - last < 60_000) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  window.location.reload();
}

if (typeof window !== "undefined") {
  // Vite fires this when a lazy chunk or its dependencies fail to load.
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    handleChunkLoadFailure();
  });
  window.addEventListener("error", (event) => {
    if (isChunkLoadError(event.message)) handleChunkLoadFailure();
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason: any = event.reason;
    const msg = typeof reason === "string" ? reason : reason?.message;
    if (isChunkLoadError(msg)) handleChunkLoadFailure();
  });
}

// Smoothly fade out the inline HTML loader as React mounts
const loaderEl = document.querySelector(".app-loader") as HTMLElement | null;
if (loaderEl) {
  // Trigger fade-out on next frame so the transition runs
  requestAnimationFrame(() => loaderEl.classList.add("app-loader-hide"));
  setTimeout(() => loaderEl.remove(), 600);
}

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>,
);
